import { Status } from "@googlemaps/react-wrapper";
import { DebouncedFunc } from "lodash";
import { Boundaries, Coordinates } from "../useMapCenter";
import { googleMapsService } from "./geoservice-gmaps";
import { mapBoxGeoService } from "./geoservice-mapbox";

export enum GeoServices {
  GMAPS = "gmaps",
  MAPBOX = "mapbox"
}

export interface GeoResultComponent {
  shortName: string;
  longName: string;
  type: "country";
}

export interface GeoResult {
  id: string;
  address: string;
  center: Coordinates;
  components: GeoResultComponent[];
  name: string;
  raw: unknown;
  types: string[];
}

export const isGeoResult = (input: unknown): input is GeoResult => {
  return (
    typeof input === "object" &&
    input !== null &&
    "id" in input &&
    "address" in input &&
    "center" in input &&
    "name" in input
  );
};

export interface GeoBiasing {
  center: Coordinates;
  boundaries?: Boundaries;
}

export type GeoSearchTerm = Coordinates | string;

export interface GeoService {
  search: (term: GeoSearchTerm, biasing?: GeoBiasing) => Promise<GeoResult[]>;
  debouncedSearch: DebouncedFunc<GeoService["search"]>;
}

export const getGeoService = (service: GeoServices) => {
  if (service === GeoServices.GMAPS) return googleMapsService;

  if (service === GeoServices.MAPBOX) return mapBoxGeoService;

  throw new Error("Not implemented provider");
};

export const getDefaultGeoService = (
  GMapsStatus: Status,
  ForceGeoServiceName?: GeoServices | null
): GeoService => {
  if (ForceGeoServiceName && ForceGeoServiceName !== null) {
    return getGeoService(ForceGeoServiceName);
  }

  if (process.env.REACT_APP_DEFAULT_GEO_SERVICE === GeoServices.MAPBOX) {
    return getGeoService(GeoServices.MAPBOX);
  }

  if (
    process.env.REACT_APP_DEFAULT_GEO_SERVICE === GeoServices.GMAPS &&
    GMapsStatus === Status.SUCCESS
  )
    return getGeoService(GeoServices.GMAPS);

  return getGeoService(GeoServices.MAPBOX);
};
